import { DestaqueDoFunci } from 'services/destaques.service';
import { FOCO_VISIVEL } from 'utils/foco';

import {
  arteDoSelo,
  corDoCartao,
  destaquesPorExtenso,
  nomeExibido,
  TIPOS_DE_DESTAQUE,
  tiposConquistados,
} from '../destaques-modelo';
import AvatarDoFunci from './AvatarDoFunci';

interface CartaoDoFunciProps {
  funci: DestaqueDoFunci;
  /** Título do ciclo ("1S2026"): escolhe a arte dos selos. */
  ciclo: string;
  onAbrir: (funci: DestaqueDoFunci) => void;
}

/**
 * Funci na lista de Destaques (`Galeria.dc.html`, DST-07): o rosto, o nome, a diretoria e os três selos, os não
 * conquistados em cinza. O fundo diz quantos tipos ele conquistou. "Ver mais" em `brand-blue-dark`: o azul do
 * design sobre o amarelo dava 4,43:1.
 *
 * O nome do botão sai do conteúdo, e não de um `aria-label`: assim ele contém todo o texto visível (WCAG 2.5.3,
 * `label_name_visible` do IBM). Os destaques vão por extenso num texto só para o leitor de tela.
 */
export default function CartaoDoFunci({ funci, ciclo, onAbrir }: CartaoDoFunciProps) {
  const conquistados = tiposConquistados(funci);

  return (
    <button
      type="button"
      onClick={() => onAbrir(funci)}
      className={`flex min-w-0 cursor-pointer flex-col items-center gap-[7px] rounded-[18px] border-0 px-[18px] pb-5 pt-[26px] text-center font-text shadow-jj-01 transition hover:-translate-y-0.5 hover:shadow-jj-card-lift motion-reduce:transition-none motion-reduce:hover:translate-y-0 ${corDoCartao(funci.qtdTipos)} ${FOCO_VISIVEL}`}
    >
      <span aria-hidden="true" className="mb-1 h-20 w-20 shrink-0 overflow-hidden rounded-jj-full bg-surface-0 shadow-jj-01">
        <AvatarDoFunci funci={funci} />
      </span>
      <span className="font-display text-[17px] font-extrabold leading-[1.15] text-brand-blue-dark [overflow-wrap:anywhere]">
        {nomeExibido(funci)}
      </span>
      {/* Os espaços entre os trechos separam as palavras no nome acessível (os trechos são itens de flex). */}{' '}
      {funci.diretoria && <span className="text-xs leading-[1.35] text-brand-navy">{funci.diretoria}</span>}{' '}
      <span className="sr-only">Destaques: {destaquesPorExtenso(funci)}.</span>{' '}
      <span aria-hidden="true" className="mt-2.5 flex justify-center gap-2">
        {TIPOS_DE_DESTAQUE.map(tipo => {
          const conquistou = conquistados.includes(tipo);
          const arte = arteDoSelo(tipo, ciclo, tipo === 'cultura' ? funci.cultura : undefined);
          return (
            <span key={tipo} data-selo={tipo} className={`flex h-[58px] w-[58px] ${conquistou ? '' : 'opacity-[.35] grayscale'}`}>
              {arte ? (
                <img src={arte} alt="" className="h-full w-full object-contain" />
              ) : (
                <span className="h-full w-full rounded-jj-full bg-surface-200" />
              )}
            </span>
          );
        })}
      </span>
      <span className="mt-2 inline-flex items-center gap-1.5 font-display text-[11px] font-bold uppercase tracking-[0.04em] text-brand-blue-dark">
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.6"
          strokeLinecap="round"
          aria-hidden="true"
          focusable="false"
        >
          <path d="M12 5v14" />
          <path d="M5 12h14" />
        </svg>
        Ver mais
      </span>
    </button>
  );
}
